/* eslint-disable @typescript-eslint/naming-convention */
import { useContext, useState, useCallback } from "react";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";

// helpers
import { getButtonTitle } from "@swiggy-private/cart-ui";
import {
    getHalfCardCondition,
    getPlan,
    getShowHalfCardItem,
    isValidType,
    shouldShowHalfCard,
} from "@cart-client/helpers/OfferBundle";
import {
    getSidForWhichHalfCardIsVisible,
    currentSid,
    updateSidForWhichHalfCardIsVisible,
} from "@cart-client/helpers/OfferHalfCard";
import { isEmpty, useEffectOnce } from "@swiggy-private/core-ui";

// interfaces
import { ButtonTitles, IExtraItemContext, IItem, IUpdateItem, SelectedOption } from "@swiggy-private/cart-types";
import { UpdateCartData } from "@cart-client/interfaces/Checkout";
import { InitialState } from "./types";

export const useOfferBundle = (initialState: InitialState = {}): IExtraItemContext => {
    const { cartState } = useContext(CartContext);
    const { localCart } = cartState;
    const { doUpdateCart } = useContext(UpdateCartContext);
    const [showHalfCard, setShowHalfCard] = useState<IItem | boolean>(false);

    const hasShownHalfCardInSession = useCallback(async () => await getSidForWhichHalfCardIsVisible(), []);

    useEffectOnce(() => {
        if (initialState?.showHalfCard) {
            // Show half card on mount for same sid just once
            hasShownHalfCardInSession().then(sid => {
                if (sid !== currentSid() && !isEmpty(initialState.showHalfCard)) {
                    setShowHalfCard(initialState.showHalfCard);
                    updateSidForWhichHalfCardIsVisible(currentSid());
                }
            });
        }
    });

    const updateCart = useCallback(
        async (payload: UpdateCartData) => {
            await doUpdateCart({ ...localCart, ...payload });
        },
        [doUpdateCart, localCart],
    );

    const performUpdateCart = useCallback(
        async (
            item?: IUpdateItem,
            selectedOption?: SelectedOption | null,
            type?: string,
            isOfferPackAdded?: boolean,
        ) => {
            if (!item || !item.nudge_type) return null;

            const isUpdateCartFromHalfCardRequired = getHalfCardCondition(item, selectedOption);

            if (isUpdateCartFromHalfCardRequired) {
                const buttonState = getButtonTitle(item, type, isOfferPackAdded);

                if (buttonState === ButtonTitles.REMOVE) {
                    await updateCart({
                        subscriptionItems: [],
                        is_membership_plan_removed: true,
                    });
                } else if (buttonState === ButtonTitles.ADD) {
                    await updateCart({
                        subscriptionItems: getPlan(item),
                        is_membership_plan_removed: false,
                    });
                }
            }
        },
        [updateCart],
    );

    const onUpdateItem = useCallback(
        async (item: IUpdateItem, type?: string, isOfferPackAdded?: boolean) => {
            if (isValidType(type)) {
                await performUpdateCart(item, null, type, isOfferPackAdded);
                return;
            }

            if (shouldShowHalfCard(item)) {
                setShowHalfCard(item);
                return;
            }

            await performUpdateCart(item);
        },
        [performUpdateCart, setShowHalfCard],
    );

    const onHalfCardConfirm = useCallback(
        async (selectedOption: number) => {
            const updateItem = getShowHalfCardItem(showHalfCard);
            if (!updateItem || typeof updateItem === "boolean") {
                return;
            }
            await performUpdateCart(updateItem, selectedOption);
            setShowHalfCard(false);
        },
        [performUpdateCart, setShowHalfCard, showHalfCard],
    );

    const onHalfCardClose = useCallback(() => setShowHalfCard(false), [setShowHalfCard]);

    return { updating: false, onUpdateItem, showHalfCard, onHalfCardClose, onHalfCardConfirm };
};
